import { isValidElement, type ReactNode } from 'react';

export type SortVal = number | string | null;

export function textOf(n: ReactNode): string {
  if (n === null || n === undefined || typeof n === 'boolean') return '';
  if (typeof n === 'string' || typeof n === 'number' || typeof n === 'bigint') return String(n);
  if (Array.isArray(n)) return n.map(textOf).join('');
  if (isValidElement(n)) return textOf((n.props as { children?: ReactNode }).children);
  return '';
}

const UNIT: Record<string, number> = {
  b: 1, kb: 1e3, mb: 1e6, gb: 1e9, tb: 1e12, kib: 1024, mib: 1024 ** 2, gib: 1024 ** 3, tib: 1024 ** 4,
  ms: 0.001, s: 1, sec: 1, secs: 1, m: 60, min: 60, mins: 60, h: 3600, hr: 3600, hrs: 3600, d: 86400, day: 86400, days: 86400, w: 604800, wk: 604800,
};
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function duration(s: string): number | null {
  const parts = s.match(/\d+(?:\.\d+)?\s*[a-z]+/g);
  if (!parts || parts.join('').replace(/\s/g, '') !== s.replace(/\s/g, '')) return null;
  let total = 0;
  for (const p of parts) {
    const m = p.match(/^(\d+(?:\.\d+)?)\s*([a-z]+)$/)!;
    const u = UNIT[m[2]];
    if (u === undefined || u > 604800 || m[2].endsWith('b')) return null;
    total += parseFloat(m[1]) * u;
  }
  return total;
}

function localDate(s: string): number | null {
  const m = s.match(/^(?:([a-z]{3})[a-z]*\.? (\d{1,2})|(\d{1,2}) ([a-z]{3})[a-z]*\.?),?(?: (\d{4}),?)?(?:,? (?:at )?(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]\.?m\.?)?)?$/);
  if (!m) return null;
  const mon = MONTHS.indexOf(m[1] ?? m[4]);
  if (mon < 0) return null;
  const day = +(m[2] ?? m[3]);
  let h = m[6] ? +m[6] : 0;
  if (m[9]) h = (h % 12) + (m[9].startsWith('p') ? 12 : 0);
  const now = new Date();
  const at = (y: number) => new Date(y, mon, day, h, m[7] ? +m[7] : 0, m[8] ? +m[8] : 0).getTime();
  if (m[5]) return at(+m[5]);
  const t = at(now.getFullYear());
  return t > now.getTime() + 86400_000 ? at(now.getFullYear() - 1) : t;
}

export function valueOf(raw: string): SortVal {
  const s = raw.trim().toLowerCase().replace(/−/g, '-').replace(/ | /g, ' ');
  if (!s || s === '—' || s === '-' || s === '–' || s === 'n/a') return null;
  const ago = s.endsWith(' ago');
  const body = ago ? s.slice(0, -4).trim() : s;
  const d = duration(body);
  if (d !== null) return ago ? -d : d;
  const n = body.match(/^([+-])?\s*[$€£]?\s*(\d[\d,]*(?:\.\d+)?|\.\d+)\s*(%|×|x|[a-z/]+(?:\s+[a-z/]+)*)?$/);
  if (n) {
    let v = parseFloat(n[2].replace(/,/g, ''));
    const u = n[3] ? UNIT[n[3]] : undefined;
    if (u !== undefined) v *= u;
    if (n[1] === '-') v = -v;
    return ago ? -v : v;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) { const t = Date.parse(raw.trim()); if (!Number.isNaN(t)) return t; }
  const ld = localDate(s);
  if (ld !== null) return ld;
  return s;
}

export function compare(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'number') return -1;
  if (typeof b === 'number') return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows<R>(rows: R[], val: (r: R) => SortVal, dir: 'asc' | 'desc'): R[] {
  const keyed = rows.map((r, i) => ({ r, i, v: val(r) }));
  keyed.sort((x, y) => {
    if (x.v === null || y.v === null) return x.v === y.v ? x.i - y.i : x.v === null ? 1 : -1;
    const c = compare(x.v, y.v);
    return c === 0 ? x.i - y.i : dir === 'asc' ? c : -c;
  });
  return keyed.map((k) => k.r);
}
